import React from 'react'
import ExpertiseSection from '../components/sections/ExpertiseSection'

const ExpertiseDemoPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Expertise Section Component
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Elegant about/expertise section based on Figma design. Features sophisticated typography, 
            animated elements, and clean layout perfect for premium brand positioning.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Design Fidelity</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Exact Figma background: rgb(246, 246, 241)</li>
                <li>• Proper text colors: rgb(30, 29, 28)</li>
                <li>• 1200px max-width container</li>
                <li>• Responsive grid layouts</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Animations</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Staggered element entrance</li>
                <li>• Smooth scroll-based triggers</li>
                <li>• Hover interactions</li>
                <li>• Performance optimized</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Hudson Club Content</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Home management focus</li>
                <li>• Premium service messaging</li>
                <li>• Luxury positioning</li>
                <li>• Professional tone</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Component Showcase */}
      <ExpertiseSection />

      {/* Usage Examples */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Integration & Usage
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Perfect Placements
              </h3>
              <div className="space-y-3 text-gray-600">
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>About Us page hero section</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>Services overview introduction</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>Company expertise showcase</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>Landing page value proposition</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>Between hero and detailed services</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Customization Options
              </h3>
              <div className="space-y-3 text-gray-600">
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>Service offerings can be modified</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>Main headline easily customizable</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>CTA button can be linked</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>Colors match brand palette</span>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-3 mt-2 flex-shrink-0"></div>
                  <span>Animation delays adjustable</span>
                </div>
              </div>
            </div>
          </div>

          {/* Code Example */}
          <div className="mt-12 bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Simple Integration
            </h3>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto">
              <code>{`import ExpertiseSection from '../components/sections/ExpertiseSection'

const AboutPage = () => {
  return (
    <div>
      <ExpertiseSection />
    </div>
  )
}`}</code>
            </pre>
          </div>

          {/* Design Specifications */}
          <div className="mt-12 bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Design Specifications
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="font-medium text-gray-900">Background</div>
                <div className="text-gray-600">rgb(246, 246, 241)</div>
              </div>
              <div>
                <div className="font-medium text-gray-900">Text Color</div>
                <div className="text-gray-600">rgb(30, 29, 28)</div>
              </div>
              <div>
                <div className="font-medium text-gray-900">Max Width</div>
                <div className="text-gray-600">1200px</div>
              </div>
              <div>
                <div className="font-medium text-gray-900">Padding</div>
                <div className="text-gray-600">24px vertical</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExpertiseDemoPage 